import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { Grid } from 'src/components/Grid';
import { Meta } from 'src/components/Meta';
import { Typography } from 'src/components/Typography';
import {
  fetchPlanWorkoutForLeaderboard,
  fetchPlanWorkoutOverviewResults,
} from 'src/models/leaderboard/leaderboard';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

import { LeaderboardFilterButton } from './components/LeaderboardFilterButton';
import { LeaderboardSummary } from './components/LeaderboardSummary';
import { formatExerciseTime } from './components/leaderboardUtils';
import { useLeaderboardFilters } from './components/useLeaderboardFilters';

export const LeaderboardWorkoutPage = () => {
  const { workoutId } = useParams<{ workoutId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tvMode = searchParams.get('tvMode') === 'true';
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });

  const { filters } = useLeaderboardFilters();

  const { data: workout, isLoading: isLoadingWorkout } = useQuery({
    queryKey: ['planWorkoutForLeaderboard', workoutId],
    queryFn: () => fetchPlanWorkoutForLeaderboard(workoutId!),
    enabled: !!workoutId,
  });

  const { data: results, isLoading: isLoadingResults } = useQuery({
    queryKey: ['planWorkoutOverviewResults', workoutId, filters],
    queryFn: () =>
      fetchPlanWorkoutOverviewResults({
        planWorkoutId: workoutId!,
        ...filters,
      }),
    enabled: !!workoutId,
  });

  const scoreType = workout?.score_type;
  const isNonNumeric = scoreType === 'rounds_reps' || scoreType === 'time';

  const divisions = results?.divisions
    ? Object.keys(results.divisions).map((key) => ({
        type: 'rx' as const,
        key,
        label: key === 'rx' ? 'Rx' : 'Scaled',
        description: '',
        results: (results.divisions[key] || []).map((r: any) => {
          const result = { ...r };
          if (scoreType === 'rounds_reps') {
            result.value = `${r.value} rounds, ${r.subvalue || 0} reps`;
          } else if (scoreType === 'time') {
            result.value = formatExerciseTime(r.value);
          }
          return result;
        }),
      }))
    : [];

  const isLoading = isLoadingWorkout || isLoadingResults;

  return (
    <>
      <Meta title={workout?.name || t('workout')} />
      <AppFrameConfig
        id="leaderboard-workout"
        containerMaxWidth="lg"
        title={workout?.name || ''}
        backButtonLinkTo="/leaderboards/public/calendars"
        backButtonTitle={t('title')}
      />
      <Container>
        <Box sx={{ mx: 2, my: 3 }}>
          <Typography variant="h6">{workout?.name}</Typography>
          {workout?.notes && (
            <Typography variant="body2" color="text.secondary">
              {workout.notes}
            </Typography>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {divisions.map((division) => (
              <Grid key={division.key} size={{ xs: 12, md: 6 }}>
                <Box sx={{ px: 1, mb: 3 }}>
                  <LeaderboardSummary
                    division={division}
                    results={division.results}
                    tvMode={tvMode}
                    numeric={!isNonNumeric}
                    onDivisionClick={(div) =>
                      navigate(
                        `/leaderboards/public/workout/${workoutId}/${div.type}/${div.key}`
                      )
                    }
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <LeaderboardFilterButton
          availableFilters={['location', 'age', 'gender']}
        />
      </Container>
    </>
  );
};
